import React from 'react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@shared/ui/accordion";
import { FileText, DollarSign, Link as LinkIcon, Receipt, Activity, AlertTriangle } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from '@shared/selectors/globalSelectors';
import { useTranslation } from 'react-i18next';

// We will construct this dynamically now

export function ChargesFeesBreakdown({ planConfig }: { planConfig?: any }) {
  const currency= useCurrency()
  const { t } = useTranslation();
  const dynamicChargesData = React.useMemo(() => {
    if (!planConfig?.result) return []; // Default to empty array

    const charges = planConfig.result.charges || {};
    const penalties = planConfig.result.penalties?.rules || [];

    const mapItem = (item: any) => {
      let amountStr = t("NA");
      if (item.amount !== null && item.amount !== undefined) {
         amountStr = `${currency} ${item.amount}`;
      } else if (item.rate !== null && item.rate !== undefined && item.rate > 0) {
         amountStr = `${item.rate}%`;
      } else if (item.chargeRate && item.chargeRate.length > 0) {
         amountStr = `${currency} ${item.chargeRate[0].rate}`;
      }

      let appliesToStr = t("NA");
      if (item.mode) appliesToStr = item.mode.replace(/_/g, ' ');
      if (item.includeIn) appliesToStr = item.includeIn;
      

      return {
        name: item.name || item.penaltyName || item.code || t("Unknown"),
        amount: amountStr,
        appliesTo: appliesToStr,
        description: item.description || (item.utilityServices ? `${t("Applies to:")} ${item.utilityServices.join(', ')}` : '')
      };
    };

    return [
      {
        id: "fixed",
        title: t("Fixed Charges"),
        subtitle: t("Applied every month regardless of usage"),
        icon: DollarSign,
        theme: "blue",
        items: (charges.fixedCharges || charges.fixed_charges || []).map(mapItem)
      },
      {
        id: "linked",
        title: t("Linked Charges"),
        subtitle: t("Tied to consumption / usage readings"),
        icon: LinkIcon,
        theme: "purple",
        items: (charges.linkedCharges || charges.linked_charges || []).map(mapItem)
      },
      {
        id: "taxes",
        title: t("Taxes & Levies"),
        subtitle: t("Government-mandated taxes and levies"),
        icon: Receipt,
        theme: "gray",
        items: (charges.taxes || []).map(mapItem)
      },
      {
        id: "surcharges",
        title: t("Surcharges"),
        subtitle: t("Program-specific and regulatory surcharges"),
        icon: Activity,
        theme: "orange",
        items: (charges.surcharges || []).map(mapItem)
      },
      {
        id: "penalties",
        title: t("Penalties & Fees"),
        subtitle: t("Applied for late payment or service events"),
        icon: AlertTriangle,
        theme: "red",
        items: penalties.map(mapItem)
      }
    ].filter(section => section.items.length > 0);
  }, [planConfig, t, currency]);

  if (dynamicChargesData.length === 0) {
    return null; // hide completely if no charges data exists
  }

  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <FileText className="w-4 h-4 text-gray-400" />
        <h3 className="text-xs font-bold text-gray-500">{t("Charges & Fees Breakdown")}</h3>
        <div className="h-px bg-gray-200 flex-1 ml-2"></div>
      </div>

      <Accordion type="single" collapsible defaultValue="fixed" className="space-y-4">
        {dynamicChargesData.map((section) => {
          const Icon = section.icon;
          const themeClasses: Record<string, any> = {
            blue: {
              item: "border-blue-100 [&[data-state=open]]:border-blue-200",
              trigger: "bg-blue-50/30 hover:bg-blue-50/60 data-[state=open]:bg-blue-50/50 [&>svg]:text-gray-400",
              icon: "text-blue-600",
              title: "text-blue-900",
              content: "border-blue-100"
            },
            purple: {
              item: "border-purple-100 [&[data-state=open]]:border-purple-200",
              trigger: "bg-purple-50/30 hover:bg-purple-50/60 data-[state=open]:bg-purple-50/50 [&>svg]:text-gray-400",
              icon: "text-purple-600",
              title: "text-purple-900",
              content: "border-purple-100"
            },
            gray: {
              item: "border-gray-200 [&[data-state=open]]:border-gray-300",
              trigger: "bg-gray-50/30 hover:bg-gray-50/60 data-[state=open]:bg-gray-50/50 [&>svg]:text-gray-400",
              icon: "text-gray-500",
              title: "text-gray-800",
              content: "border-gray-200"
            },
            orange: {
              item: "border-orange-100 [&[data-state=open]]:border-orange-200",
              trigger: "bg-orange-50/30 hover:bg-orange-50/60 data-[state=open]:bg-orange-50/50 [&>svg]:text-gray-400",
              icon: "text-orange-500",
              title: "text-orange-900",
              content: "border-orange-100"
            },
            red: {
              item: "border-red-100 [&[data-state=open]]:border-red-200",
              trigger: "bg-red-50/30 hover:bg-red-50/60 data-[state=open]:bg-red-50/50 [&>svg]:text-gray-400",
              icon: "text-red-500",
              title: "text-red-900",
              content: "border-red-100"
            }
          };

          const tc = themeClasses[section.theme] || themeClasses.gray;
          const itemCount = section.items.length; 
          
          return (
            <AccordionItem key={section.id} value={section.id} className={cn("rounded-xl overflow-hidden bg-white shadow-sm transition-colors border", tc.item)}>
              <AccordionTrigger className={cn("hover:no-underline px-5 py-4 transition-colors", tc.trigger)}>
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <Icon className={cn("w-5 h-5 shrink-0", tc.icon)} />
                    <div className="text-left">
                      <p className={cn("font-bold text-[15px]", tc.title)}>{section.title}</p>
                      <p className="text-xs text-gray-500 font-medium">{section.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 font-medium">{itemCount} {t("items")}</p>
                </div>
              </AccordionTrigger>
              <AccordionContent className={cn("p-0 border-t", tc.content)}>
                {section.items.length > 0 ? (
                  <div className="overflow-x-auto border-t-0">
                    <GenericTable
                      data={section.items}
                      columns={[
                        {
                          key: "name",
                          header: t("Charge"),
                          type: "custom",
                          render: (value: any) => <span className="font-bold text-[13px] text-gray-900">{value}</span>
                        },
                        {
                          key: "amount",
                          header: t("Amount / Rate"),
                          type: "custom",
                          render: (value: any, row: any) => (
                            <span className="font-bold text-[13px] text-gray-900">
                              {value}<span className="text-[11px] text-gray-400 font-semibold">{row.frequency}</span>
                            </span>
                          )
                        },
                        {
                          key: "appliesTo",
                          header: t("Applies To"),
                          type: "custom",
                          render: (value: any) => <span className="text-[12px] text-gray-500 font-medium">{value}</span>
                        },
                        {
                          key: "description",
                          header: t("Description"),
                          type: "custom",
                          render: (value: any) => <span className="text-[12px] text-gray-500 font-medium">{value}</span>
                        }
                      ]}
                      rowKey="name"
                    />
                  </div>
                ) : (
                  <div className="p-6 text-sm text-gray-500 italic text-center">{t("No active charges in this category")}</div>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
